import {
  compatWrapper,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import {
  PageBlueprint,
  createFrontendPlugin,
  type FrontendPlugin,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import SecurityIcon from '@mui/icons-material/Security';

import { entityContentRouteRef, snykOverviewRouteRef } from './routes';
import { isSnykAvailableEntityPredicate } from './shared/lib/entity-predicates';

const snykBranchViewEntityContent = EntityContentBlueprint.make({
  params: {
    path: '/snyk-by-branch',
    title: 'Snyk',
    routeRef: convertLegacyRouteRef(entityContentRouteRef),
    filter: isSnykAvailableEntityPredicate,
    async loader() {
      const { SnykEntity } = await import('./features/entity-tab');
      return compatWrapper(<SnykEntity />);
    },
  },
});

// Standalone page rather than an entity tab: the question it answers - "what
// Snyk issues are open, across which Service Groups" - spans organizations and
// repositories, so it has no single owning entity. Its "Issue scope" toggle
// narrows it down to the hardcoded-secrets view this page used to be
// exclusively.
//
// `title` and `icon` are what put this in the sidebar. PageBlueprint emits
// core.title/core.icon only when they are supplied, and the app's Sidebar renders
// nav.rest() over the nav items that exist - it does not synthesise one from a
// route. Without these the page is reachable at its path but invisible.
const snykOverviewPage = PageBlueprint.make({
  name: 'snyk-overview',
  params: {
    path: '/snyk',
    title: 'Snyk',
    icon: <SecurityIcon />,
    routeRef: convertLegacyRouteRef(snykOverviewRouteRef),
    async loader() {
      const { SnykOverviewPage } = await import('./features/overview');
      return compatWrapper(<SnykOverviewPage />);
    },
  },
});

// System-scoped counterpart to the Snyk overview page's Summary rollup - a
// dedicated tab (not a card slotted into the default About-style layout) so
// more Snyk tables can be added to this same tab later without needing a new
// one each time. See SystemSnykContent.
const systemSnykContent = EntityContentBlueprint.make({
  name: 'system-snyk',
  params: {
    path: '/snyk-vulnerabilities',
    title: 'Snyk Vulnerabilities',
    filter: { kind: 'system' },
    async loader() {
      const { SystemSnykContent } = await import('./features/system-card');
      return <SystemSnykContent />;
    },
  },
});

// ServiceGroup-page counterpart to systemSnykContent above - a ServiceGroup
// carries its snyk.io/org-id annotation directly, so this shows that one
// org's vulnerabilities rather than rolling up several. See
// ServiceGroupSnykContent.
const serviceGroupSnykContent = EntityContentBlueprint.make({
  name: 'servicegroup-snyk',
  params: {
    path: '/snyk-vulnerabilities',
    title: 'Snyk Vulnerabilities',
    filter: { kind: 'servicegroup' },
    async loader() {
      const { ServiceGroupSnykContent } = await import(
        './features/service-group-card'
      );
      return <ServiceGroupSnykContent />;
    },
  },
});

export const snykBranchViewPlugin: FrontendPlugin = createFrontendPlugin({
  pluginId: 'snyk-branch-view',
  extensions: [
    snykBranchViewEntityContent,
    snykOverviewPage,
    systemSnykContent,
    serviceGroupSnykContent,
  ],
  routes: convertLegacyRouteRefs({
    entityContent: entityContentRouteRef,
    snykOverview: snykOverviewRouteRef,
  }),
});
